import { siteName } from 'config/site';
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Paper, Accordion,
  AccordionSummary, AccordionDetails, Button, Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const faqs = [
  {
    q: `Is ${siteName} completely free?`,
    a: 'Creating and editing resumes is completely free — unlimited resumes, unlimited edits. You only pay when you are ready to download your final resume. One payment gives you 90-day access to download and update your resume as many times as you need.',
  },
  {
    q: 'Are the resumes ATS-friendly?',
    a: 'Yes. Every template is designed for ATS compatibility — single-column layout, standard section headings, and no graphics or text boxes. Our keyword optimization system matches your resume to the specific job description, achieving a 95% ATS pass rate in our tests.',
  },
  {
    q: 'How does the AI write my resume?',
    a: 'You provide your job title, company, dates, and a rough description of your work. Our AI (GPT-4) transforms that into polished, quantified achievement bullets using proven resume writing frameworks. A secondary optimization pass integrates keywords from your target job description.',
  },
  {
    q: 'How long does it take to create a resume?',
    a: 'Most users complete their first resume in 15–30 minutes. The AI handles the heavy writing — you just review, edit, and refine.',
  },
  {
    q: `Can I use ${siteName} for any industry?`,
    a: 'Yes. The AI generates content for any profession — software engineering, marketing, nursing, finance, teaching, operations, and more. It adapts its language and structure to your specific industry automatically.',
  },
  {
    q: 'What file format does the resume download as?',
    a: 'Your resume downloads as a text-layer PDF — fully ATS-readable and professional in appearance. The PDF is rendered using Playwright (headless Chrome) for pixel-perfect output.',
  },
];

const stats = [
  { value: '95%', label: 'ATS Pass Rate' },
  { value: '30 min', label: 'Avg Build Time' },
  { value: '10K+', label: 'Resumes Created' },
  { value: '4.8★', label: 'User Rating' },
];

const guides = [
  { path: '/ats-friendly-resume', label: 'ATS-Friendly Resume Guide' },
  { path: '/resume-format-guide', label: 'Resume Format Guide' },
  { path: '/resume-summary-examples', label: 'Resume Summary Examples' },
  { path: '/how-to-make-resume', label: 'How to Make a Resume' },
  { path: '/resume-keywords-guide', label: 'Resume Keywords Guide' },
  { path: '/resume-mistakes', label: '10 Resume Mistakes to Avoid' },
];

const HomepageSEOContent = () => (
  <Box
    component="section"
    sx={{ bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider' }}
    aria-label="Resume builder information"
  >
    {/* Stats bar */}
    <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', py: 3 }}>
      <Container maxWidth="lg">
        <Grid container spacing={2} justifyContent="center">
          {stats.map((s) => (
            <Grid item xs={6} sm={3} key={s.value} sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={800} color="primary.main">{s.value}</Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={500}>{s.label}</Typography>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
      {/* Value proposition */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h2" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 800, mb: 2, color: 'text.primary' }}>
          The AI Resume Builder That Gets You Interviews
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', lineHeight: 1.8 }}>
          {siteName} uses GPT-4 to generate professional, ATS-optimized resume content in seconds.
          No subscription. No templates that look like everyone else's. Just results.
        </Typography>
      </Box>

      {/* Features checklist */}
      <Paper variant="outlined" sx={{ p: 3, mb: 6, borderRadius: 3, bgcolor: 'background.paper' }}>
        <Grid container spacing={2}>
          {[
            'AI writes your bullet points automatically',
            '95% ATS pass rate on all templates',
            'Keyword optimization per job description',
            'Multiple professional templates included',
            'PDF download with text layer for ATS',
            'No subscription — pay only to download',
            '90-day access included with every download',
            'Unlimited resume creation always free',
          ].map((feature) => (
            <Grid item xs={12} sm={6} key={feature}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', mt: 0.3, flexShrink: 0 }} />
                <Typography variant="body2" color="text.primary">{feature}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Free resources */}
      <Box sx={{ mb: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h3" sx={{ fontSize: '1.35rem', fontWeight: 700, color: 'text.primary' }}>
            Free Resume Guides
          </Typography>
          <Button
            component={Link}
            to="/guides"
            size="small"
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' }}
          >
            View all guides →
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Expert resume advice backed by analysis of 500+ job descriptions.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {guides.map((g) => (
            <Chip
              key={g.path}
              label={g.label}
              component={Link}
              to={g.path}
              clickable
              variant="outlined"
              sx={{
                fontWeight: 500,
                fontSize: '0.8rem',
                color: 'text.primary',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
              }}
            />
          ))}
        </Box>
      </Box>

      {/* FAQ */}
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h2"
          component="h2"
          sx={{ fontSize: '1.5rem', fontWeight: 800, mb: 3, color: 'text.primary' }}
        >
          Frequently Asked Questions
        </Typography>
        {faqs.map((faq, i) => (
          <Accordion
            key={i}
            disableGutters
            elevation={0}
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              mb: 1,
              borderRadius: '8px !important',
              '&:before': { display: 'none' },
              '&.Mui-expanded': { borderColor: 'primary.main' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ '& .MuiAccordionSummary-content': { my: 1.5 } }}
            >
              <Typography variant="body1" fontWeight={600} color="text.primary" sx={{ fontSize: '0.95rem' }}>
                {faq.q}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                {faq.a}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* CTA */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontSize: '1.5rem', fontWeight: 800, mb: 1.5, color: 'text.primary' }}>
          Ready to Build Your Resume?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Join thousands of job seekers who got more interviews with {siteName}.
        </Typography>
        <Button
          component={Link}
          to="/"
          variant="contained"
          size="large"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 5, py: 1.5 }}
        >
          Get Started — It's Free
        </Button>
      </Box>
    </Container>
  </Box>
);

export default HomepageSEOContent;
